'use client';

import { useState, useEffect, useRef } from 'react';
import Image from '@/components/shared/Image';
import { ChevronDown, Menu, X, ArrowRight, Globe, Target } from 'lucide-react';
import { mainNavigation } from '@/data/navigation';
import { Link, usePathname, useRouter } from '@/i18n/routing';
import { useTranslations, useLocale } from '@/i18n/intl';
import { RichText } from '@/components/shared/RichText';
import { stripHtml } from '@/lib/utils';
import { resolveMediaUrl } from '@/lib/media';

interface HeaderProps {
  services: any[];
  footprints: any[];
  categories: any[];
  settings: Record<string, string>;
  insights: any[];
}

export function Header({ services, footprints, categories, settings, insights }: HeaderProps) {
  const t = useTranslations('Common');
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileDropdown, setMobileDropdown] = useState<string | null>(null);
  
  const isHome = pathname === '/' || pathname === '';
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when pathname changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setMobileDropdown(null);
  }, [pathname]);

  const isCareerEnabled = settings.enable_career !== 'false';
  
  // Dynamically build navigation from data with translations
  const dynamicNavigation = mainNavigation.map(item => {
    // Map main labels to translation keys
    let label = item.label;
    if (item.label === 'About Us') label = t('about');
    if (item.label === 'Our Expertise') label = t('expertise');
    if (item.label === 'Our People') label = t('ourPeople');
    if (item.label === 'Insights') label = t('insights');
    if (item.label === 'Life at Simisi') label = t('lifeAtSimisi');

    // Prevent top-level navigation for Our Expertise
    const mappedItem = { 
      ...item, 
      label,
      href: item.label === 'Our Expertise' ? '#' : item.href 
    };

    if (item.label === 'Our Expertise') {
      return {
        ...mappedItem,
        children: services.map(service => ({
          label: service.title,
          href: `/services/${service.slug}`
        }))
      };
    }

    if (item.label === 'Insights') {
      return {
        ...mappedItem,
        // Mega menu on desktop/mobile — not a flat children list
      };
    }

    return mappedItem;
  });

  const filteredNavigation = dynamicNavigation.filter(item => {
    if (item.label === t('lifeAtSimisi') && !isCareerEnabled) return false;
    return true;
  });

  const toggleLocale = () => {
    router.replace(pathname);
  };

  const isTransparent = isHome && !isScrolled;
  const headerBg = isTransparent
    ? 'bg-transparent'
    : 'bg-navy shadow-[0_1px_0_rgba(0,0,0,0.08)]';

  const textColor = 'text-white';

  const handleMouseEnter = (label: string, hasChildren: boolean) => {
    if (!hasChildren) return;
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setActiveDropdown(label);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 300);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}
      style={{ height: isScrolled ? '70px' : '85px' }}
    >
      <div className="max-w-[1170px] mx-auto px-5 md:px-[40px] h-full">
        <div className="flex items-center justify-between h-full">
          <Link href="/" className="flex items-center transition-opacity duration-300 hover:opacity-80">
            <Image
              src="/images/simisi/logo-simisi.svg"
              alt="Simisi&Co."
              width={180}
              height={42}
              className="h-[32px] w-auto transition-all duration-300"
              priority
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-[0.3rem]">
            {filteredNavigation.map((item) => {
              const hasChildren =
                item.label === t('expertise') ||
                item.label === t('insights') ||
                !!(item.children && item.children.length > 0);
              const isMegaDropdown = item.label === t('expertise') || item.label === t('insights');
              const isActive = pathname === item.href || pathname.startsWith(item.href + '/');

              return (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => handleMouseEnter(item.label, hasChildren)}
                  onMouseLeave={handleMouseLeave}
                >
                  <Link
                    href={item.href as any}
                    className={`flex items-center gap-1.5 px-3 py-2 text-[14px] font-medium transition-colors duration-200 hover:text-primary ${
                      isActive ? 'text-primary' : textColor
                    }`}
                  >
                    {item.label}
                    {hasChildren && <ChevronDown className="h-3.5 w-3.5 font-bold stroke-[3]" />}
                  </Link>

                  {hasChildren && activeDropdown === item.label && (
                    <div
                      className={
                        isMegaDropdown
                          ? 'absolute top-full left-1/2 -translate-x-1/2 pt-0'
                          : 'absolute top-full left-0 pt-0'
                      }
                    >
                      {item.label === t('expertise') ? (
                        <ServicesDropdown services={services} categories={categories} onClose={() => setActiveDropdown(null)} />
                      ) : item.label === t('insights') ? (
                        <InsightsDropdown footprints={footprints} insights={insights} onClose={() => setActiveDropdown(null)} />
                      ) : (
                        <SimpleDropdown items={item.children!} onClose={() => setActiveDropdown(null)} />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            {settings.enable_lms === 'true' && (
              <a
                href={settings.lms_url || 'https://lms.simisi.id'}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-1.5 px-3 py-2 text-[13px] font-bold uppercase transition-colors duration-200 hover:text-primary ${textColor}`}
              >
                {t('lms')}
              </a>
            )}
            {/* Hide language switcher for now
            <button
              onClick={toggleLocale}
              className={`flex items-center gap-1.5 px-3 py-2 text-[13px] font-bold uppercase transition-colors duration-200 hover:text-primary ${textColor}`}
            >
              <Globe className="h-4 w-4" />
              {locale.toUpperCase()}
            </button>
            */}
            <Link
              href="/contact-us"
              className="hidden md:inline-flex items-center bg-primary text-white text-[14px] font-semibold uppercase px-[25px] py-[11px] rounded-[25px] hover:bg-white hover:text-navy transition-all duration-300"
            >
              {t('contactUs')}
            </Link>

            <button
              className="lg:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className={`h-6 w-6 ${textColor}`} />
              ) : (
                <Menu className={`h-6 w-6 ${textColor}`} />
              )}
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-navy border-t border-white/10">
          <div className="max-w-[1170px] mx-auto px-5 md:px-[40px] py-4">
            {filteredNavigation.map((item) => {
              const isInsights = item.label === t('insights');
              const isExpertise = item.label === t('expertise');
              const hasChildren =
                isInsights || isExpertise || !!(item.children && item.children.length > 0);
              return (
                <div key={item.label} className="border-b border-white/10 last:border-0">
                  <div className="flex items-center justify-between">
                    <Link
                      href={item.href as any}
                      className="block py-3 text-[15px] font-medium text-white hover:text-primary transition-colors"
                    >
                      {item.label}
                    </Link>
                    {hasChildren && (
                      <button
                        onClick={() =>
                          setMobileDropdown(mobileDropdown === item.label ? null : item.label)
                        }
                        className="p-2 text-white"
                      >
                        <ChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${
                            mobileDropdown === item.label ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                    )}
                  </div>
                  {hasChildren && mobileDropdown === item.label && (
                    <div className="pb-4 pl-2">
                      {isInsights ? (
                        <MobileInsightsMenu
                          footprints={footprints}
                          onNavigate={() => setIsMobileMenuOpen(false)}
                        />
                      ) : isExpertise ? (
                        <MobileServicesMenu
                          categories={categories}
                          onNavigate={() => setIsMobileMenuOpen(false)}
                        />
                      ) : (
                        item.children!.map((child) => (
                          <Link
                            key={child.href}
                            href={child.href as any}
                            className="block py-2 text-[14px] text-white/70 hover:text-primary transition-colors"
                          >
                            {child.label}
                          </Link>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            
            {settings.enable_lms === 'true' && (
              <div className="border-b border-white/10">
                <a
                  href={settings.lms_url || 'https://lms.simisi.id'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block py-4 text-[15px] font-bold text-white hover:text-primary transition-colors"
                >
                  {t('lmsPlatform')}
                </a>
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-white/10">
              <Link
                href="/contact-us"
                className="block w-full text-center bg-primary text-white text-[14px] font-semibold px-6 py-3 rounded-[25px] hover:bg-white hover:text-navy transition-colors"
              >
                {t('contactUs')}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function SimpleDropdown({ items, onClose }: { items: { label: string; href: string }[]; onClose: () => void }) {
  return (
    <div
      className="bg-white rounded-none shadow-xl min-w-[220px] py-2 animate-in fade-in slide-in-from-top-2 duration-200 border border-light"
      onClick={onClose}
    >
      {items.map((item) => (
        <Link
          key={item.href}
          href={item.href as any}
          className="block px-5 py-3 text-[14px] text-charcoal hover:bg-light hover:text-primary transition-colors"
          onClick={onClose}
        >
          {item.label}
        </Link>
      ))}
    </div>
  );
}

function ServicesDropdown({ services, categories, onClose }: { services: any[]; categories: any[]; onClose: () => void }) {
  const [activeCategory, setActiveCategory] = useState<number>(0); 
  const currentCategory = categories[activeCategory];
  const t = useTranslations('Common');

  return (
    <div
      className="bg-white rounded-none shadow-2xl w-[820px] max-w-[92vw] animate-in fade-in slide-in-from-top-2 duration-200 border border-light overflow-hidden"
    >
      <div className="flex stretch">
        {/* Left Column - Category Listing */}
        <div className="w-[280px] border-r border-light py-6 bg-light/5">
          <p className="px-8 py-2 text-[10px] uppercase tracking-[0.2em] text-gray/60 font-bold mb-4">
            {t('expertise')}
          </p>
          <div className="space-y-1">
            {categories?.map((category, index) => (
              <button
                key={category.id}
                className={`w-full text-left px-8 py-3 text-[14px] transition-all duration-200 border-l-[3px] ${
                  activeCategory === index
                    ? 'bg-white text-primary border-primary font-bold shadow-sm'
                    : 'text-charcoal hover:bg-light/40 border-transparent'
                }`}
                onMouseEnter={() => setActiveCategory(index)}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Right Column - Category Description */}
        <div className="flex-1 p-10 bg-white">
          {currentCategory && (
            <div className="animate-in fade-in slide-in-from-right-1 duration-300">
              <h3 className="text-[24px] font-bold text-navy mb-4 font-heading leading-tight">
                {currentCategory.name}
              </h3>
              {currentCategory.description && (
                <RichText
                  html={currentCategory.description}
                  className="text-[15px] text-gray-500 leading-relaxed mb-8 max-w-[460px] [&_p]:text-gray-500"
                />
              )}
              
              {currentCategory.services && currentCategory.services.length > 0 && (
                <Link
                  href={`/services/${currentCategory.services[0].slug}`}
                  onClick={onClose}
                  className="inline-flex items-center gap-2 bg-primary text-white text-[13px] font-bold uppercase tracking-wider px-7 py-3.5 rounded-full hover:bg-navy transition-all duration-300 shadow-lg shadow-primary/20"
                >
                  {t('learnMore')} <ArrowRight className="h-4 w-4" />
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function getInsightsForFootprint(insights: any[], footprintId: string) {
  return insights.filter((insight) =>
    insight.footprints?.some((f: { id: string }) => f.id === footprintId)
  );
}

function pickFeaturedInsight(relatedInsights: any[]) {
  return relatedInsights.find((i) => i.isFeatured) ?? relatedInsights[0] ?? null;
}

function formatInsightDate(date: Date | string) {
  return new Date(date).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function InsightsDropdown({
  footprints,
  insights,
  onClose,
}: {
  footprints: any[];
  insights: any[];
  onClose: () => void;
}) {
  const t = useTranslations('Common');
  const [activeFootprint, setActiveFootprint] = useState(0);
  const currentFootprint = footprints[activeFootprint];
  const relatedInsights = currentFootprint
    ? getInsightsForFootprint(insights, currentFootprint.id)
    : [];
  const featuredInsight = pickFeaturedInsight(relatedInsights);
  const otherInsights = relatedInsights.filter((i) => i.id !== featuredInsight?.id).slice(0, 3);

  return (
    <div
      className="bg-white rounded-none shadow-2xl w-[820px] max-w-[92vw] animate-in fade-in slide-in-from-top-2 duration-200 border border-light overflow-hidden"
    >
      <div className="flex">
        <div className="w-[280px] border-r border-light py-6 bg-light/5">
          <div className="space-y-1">
            {footprints.map((footprint, index) => (
              <button
                key={footprint.id}
                type="button"
                className={`w-full text-left px-8 py-3 text-[14px] transition-all duration-200 border-l-[3px] ${
                  activeFootprint === index
                    ? 'bg-white text-primary border-primary font-bold shadow-sm'
                    : 'text-charcoal hover:bg-light/40 border-transparent'
                }`}
                onMouseEnter={() => setActiveFootprint(index)}
              >
                {footprint.title}
              </button>
            ))}
          </div>
          <div className="px-8 pt-4 mt-4 border-t border-light">
            <Link
              href="/insights"
              onClick={onClose}
              className="inline-flex items-center gap-1 text-[13px] font-semibold text-primary hover:gap-2 transition-all"
            >
              {t('allInsights')} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        <div className="flex-1 p-8 bg-white min-h-[320px]">
          {currentFootprint && (
            <div key={currentFootprint.id} className="animate-in fade-in slide-in-from-right-1 duration-300">
              <h3 className="text-[22px] font-bold text-navy mb-3 font-heading leading-tight">
                {currentFootprint.title}
              </h3>
              {currentFootprint.description && (
                <RichText
                  html={currentFootprint.description}
                  className="text-[14px] text-gray-500 leading-relaxed mb-6 max-w-[460px] [&_p]:text-gray-500"
                />
              )}

              {featuredInsight ? (
                <div className="mb-6">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gray/60 font-bold mb-4">
                    {t('featuredInsight')}
                  </p>
                  <Link
                    href={`/insights/${featuredInsight.slug}`}
                    onClick={onClose}
                    className="group flex gap-5"
                  >
                    <div className="w-[140px] shrink-0 aspect-[4/3] relative overflow-hidden rounded-lg border border-light">
                      <img
                        src={resolveMediaUrl(featuredInsight.coverImage) || '/images/simisi/page13_img1.png'}
                        alt={featuredInsight.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-[10px] font-bold text-primary tracking-[0.2em] uppercase mb-2 block">
                        {formatInsightDate(featuredInsight.publishDate)}
                      </span>
                      <h4 className="text-[16px] font-bold text-navy leading-snug mb-2 font-heading group-hover:text-primary transition-colors line-clamp-2">
                        {featuredInsight.title}
                      </h4>
                      {(featuredInsight.summary || featuredInsight.content) && (
                        <p className="text-[13px] text-gray-500 leading-relaxed line-clamp-2 font-sans font-light">
                          {stripHtml(featuredInsight.summary || featuredInsight.content)}
                        </p>
                      )}
                    </div>
                  </Link>
                </div>
              ) : (
                <p className="text-[14px] text-gray-400 italic mb-6">—</p>
              )}

              {otherInsights.length > 0 && (
                <ul className="space-y-2 mb-6 border-t border-light pt-5">
                  {otherInsights.map((insight) => (
                    <li key={insight.id}>
                      <Link
                        href={`/insights/${insight.slug}`}
                        onClick={onClose}
                        className="text-[14px] text-charcoal hover:text-primary transition-colors line-clamp-1"
                      >
                        {insight.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              <Link
                href={`/insights?industry=${currentFootprint.slug}`}
                onClick={onClose}
                className="inline-flex items-center gap-2 bg-primary text-white text-[13px] font-bold uppercase tracking-wider px-7 py-3.5 rounded-full hover:bg-navy transition-all duration-300 shadow-lg shadow-primary/20"
              >
                {t('viewIndustryInsights')} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MobileInsightsMenu({
  footprints,
  onNavigate,
}: {
  footprints: any[];
  onNavigate: () => void;
}) {
  const t = useTranslations('Common');

  return (
    <div className="space-y-1">
      {footprints.map((footprint) => (
        <Link
          key={footprint.id}
          href={`/insights?industry=${footprint.slug}`}
          onClick={onNavigate}
          className="block py-2 text-[14px] text-white/70 hover:text-primary transition-colors"
        >
          {footprint.title}
        </Link>
      ))}
      <Link
        href="/insights"
        onClick={onNavigate}
        className="block py-2 text-[14px] font-semibold text-primary"
      >
        {t('allInsights')}
      </Link>
    </div>
  );
}

function MobileServicesMenu({
  categories,
  onNavigate,
}: {
  categories: any[];
  onNavigate: () => void;
}) {
  const t = useTranslations('Common');
  const [activeCategory, setActiveCategory] = useState(0);
  const currentCategory = categories[activeCategory];

  if (categories.length === 0) {
    return (
      <Link
        href="/services"
        onClick={onNavigate}
        className="block py-2 text-[14px] text-white/70 hover:text-primary transition-colors"
      >
        {t('expertise')}
      </Link>
    );
  }

  return (
    <div className="space-y-4">
      <Link
        href="/services"
        onClick={onNavigate}
        className="inline-flex items-center gap-1 text-[13px] font-semibold text-primary"
      >
        {t('expertise')} <ArrowRight className="h-3.5 w-3.5" />
      </Link>

      <div className="space-y-1">
        {categories.map((category, index) => (
          <button
            key={category.id}
            type="button"
            onClick={() => setActiveCategory(index)}
            className={`w-full text-left px-3 py-2.5 text-[13px] rounded-md border-l-[3px] transition-all ${
              activeCategory === index
                ? 'bg-white/10 text-primary border-primary font-bold'
                : 'text-white/80 border-transparent hover:bg-white/5'
            }`}
          >
            {category.name}
          </button>
        ))}
      </div>

      {currentCategory && (
        <div key={currentCategory.id} className="rounded-lg border border-white/10 bg-white/5 p-4">
          <h3 className="text-[16px] font-bold text-white mb-2 font-heading leading-tight">
            {currentCategory.name}
          </h3>
          {currentCategory.description && (
            <RichText
              html={currentCategory.description}
              className="text-[13px] text-white/60 leading-relaxed mb-4 max-w-none [&_p]:text-white/60"
            />
          )}
          {currentCategory.services?.length > 0 && (
            <Link
              href={`/services/${currentCategory.services[0].slug}`}
              onClick={onNavigate}
              className="inline-flex items-center gap-2 bg-primary text-white text-[12px] font-bold uppercase tracking-wider px-5 py-2.5 rounded-full hover:bg-white hover:text-navy transition-all"
            >
              {t('learnMore')} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
